import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, X, AlertTriangle, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { fmtDate, STATUS_LABELS } from "@/lib/format";

const daysLabel = (d) => (d < 0 ? `en retard de ${-d} jour${-d > 1 ? "s" : ""}` : d === 0 ? "aujourd'hui" : d === 1 ? "demain" : `dans ${d} jours`);

export const DeadlineBanner = () => {
  const { companyId } = useCompany();
  const [items, setItems] = useState([]);
  const [hidden, setHidden] = useState("");
  const [sending, setSending] = useState(false);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/rappels/envoyer`);
      toast.success(`Rappel envoyé à ${data.destinataires.join(", ")}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  useEffect(() => {
    if (!companyId) return;
    setItems([]);
    api.get(`/companies/${companyId}/alertes`).then((r) => setItems(r.data)).catch(() => {});
  }, [companyId]);

  if (!items.length || hidden === companyId) return null;
  const overdue = items.some((i) => i.jours_restants < 0);

  return (
    <div className={`mx-8 mt-6 rounded-md border px-4 py-3 flex items-start gap-3 fade-up ${overdue ? "bg-red-50 border-red-200 dark:bg-red-500/10 dark:border-red-500/40" : "bg-brand-50 border-brand-200 dark:bg-brand-500/10 dark:border-brand-500/40"}`} data-testid="deadline-banner">
      {overdue ? <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" /> : <BellRing className="h-5 w-5 text-brand-600 dark:text-brand-300 mt-0.5 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-foreground">Rappel d'échéance{items.length > 1 ? "s" : ""} — taxe sur les salaires</div>
        <ul className="mt-1 space-y-1">
          {items.map((i) => (
            <li key={`${i.annee}-${i.type}-${i.periode}`} className="text-sm text-foreground/80 flex flex-wrap items-center gap-x-2" data-testid="deadline-item">
              <span className="font-medium">{i.libelle}</span>
              <span className="font-mono text-xs text-muted-foreground">{i.formulaire}</span>
              <span>· à déposer le <span className="font-mono">{fmtDate(i.date)}</span></span>
              <span className={`font-semibold ${i.jours_restants < 0 ? "text-red-600 dark:text-red-400" : "text-brand-600 dark:text-brand-300"}`}>({daysLabel(i.jours_restants)})</span>
              {i.declaration_id ? (
                <Link to={`/declarations/${i.declaration_id}`} className="underline hover:text-brand-600" data-testid="deadline-open-link">Ouvrir la déclaration ({STATUS_LABELS[i.status]})</Link>
              ) : (
                <Link to="/declarations" className="underline hover:text-brand-600" data-testid="deadline-create-link">Générer la déclaration</Link>
              )}
            </li>
          ))}
        </ul>
        <button onClick={sendNow} disabled={sending} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-600 dark:text-brand-300 hover:underline disabled:opacity-50" data-testid="deadline-send-email-button"><Mail className="h-3.5 w-3.5" /> {sending ? "Envoi…" : "Envoyer ce rappel par e-mail maintenant"}</button>
      </div>
      <button onClick={() => setHidden(companyId)} className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Masquer" data-testid="deadline-banner-dismiss"><X className="h-4 w-4" /></button>
    </div>
  );
};
